// The app's root error boundary: a render error shows a calm full-screen card (in the app's look) instead of a blank
// page, and goes to Sentry when it's on. A lazy chunk missing after a deploy gets "a new version is ready" instead.
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Logo } from "../brand/Logo";
import { captureError, isStaleBuildError } from "../monitoring";
import "./ErrorBoundary.css";

type State = { error: unknown | null };

export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    if (!isStaleBuildError(error)) captureError(error, { componentStack: info.componentStack ?? undefined, boundary: "app-root" });
  }

  render() {
    if (this.state.error === null) return this.props.children;
    return <CrashScreen stale={isStaleBuildError(this.state.error)} />;
  }
}

export function CrashScreen({ stale }: { stale: boolean }) {
  return (
    <main className="crash" role="alert">
      <div className="crash-card">
        <Logo layout="mark" height={44} />
        <h1 className="crash-title">{stale ? "A new version is ready" : "Something broke on our side"}</h1>
        <p className="crash-text">
          {stale
            ? "CAO Studio was updated while you had it open. Reload to pick up the new version."
            : "Sorry about that. Your projects, videos and credits are safe: reload to pick up where you left off."}
        </p>
        <div className="crash-actions">
          <button type="button" className="btn btn-primary" onClick={() => location.reload()}>
            Reload
          </button>
          {!stale && (
            <button type="button" className="btn btn-quiet" onClick={() => location.assign("/")}>
              Go to Home
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
